import { useEffect, useRef, useState } from 'react';
import { Microphone, PaperPlaneTilt } from '@phosphor-icons/react';
import { DeviceContextTray } from './DeviceContextTray';

const LIVE_WAVE_HISTORY = 32;

function formatDuration(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function normalizeVoiceLevel(value) {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return null;

  // O bridge nativo pode entregar nível normalizado (0–1), percentual ou RMS
  // em dB. A normalização mantém o componente pronto para todos os formatos
  // sem impor um contrato ao motor de voz.
  if (raw >= 0 && raw <= 1) return raw;
  if (raw > 1 && raw <= 20) return Math.max(0, Math.min(1, raw / 12));
  return Math.max(0, Math.min(1, raw / 100));
}

function drawRoundedBar(context, x, y, width, height, radius) {
  const corner = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + corner, y);
  context.lineTo(x + width - corner, y);
  context.quadraticCurveTo(x + width, y, x + width, y + corner);
  context.lineTo(x + width, y + height - corner);
  context.quadraticCurveTo(x + width, y + height, x + width - corner, y + height);
  context.lineTo(x + corner, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - corner);
  context.lineTo(x, y + corner);
  context.quadraticCurveTo(x, y, x + corner, y);
  context.closePath();
}

/**
 * Medidor de áudio da captura manual.
 *
 * Cada barra representa uma amostra recente do RMS entregue pelo adaptador
 * nativo. Não há formas pré-definidas, animação aleatória ou áudio sintético:
 * se o ambiente está em silêncio, o medidor permanece baixo; quando há voz,
 * ele acompanha o nível real suavizado para a leitura ficar leve.
 */
function LiveAudioWaveform({ level, isActive }) {
  const canvasRef = useRef(null);
  const targetLevelRef = useRef(0);
  const activeRef = useRef(isActive);

  useEffect(() => {
    targetLevelRef.current = normalizeVoiceLevel(level) ?? 0;
  }, [level]);

  useEffect(() => {
    activeRef.current = isActive;
    if (!isActive) targetLevelRef.current = 0;
  }, [isActive]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return undefined;

    const samples = Array(LIVE_WAVE_HISTORY).fill(0);
    let animationFrame = 0;
    let lastFrameAt = performance.now();
    let lastSampleAt = 0;
    let displayedLevel = 0;
    let width = 0;
    let height = 0;

    const resizeCanvas = () => {
      const bounds = canvas.getBoundingClientRect();
      const nextWidth = Math.max(1, Math.round(bounds.width));
      const nextHeight = Math.max(1, Math.round(bounds.height));
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

      if (nextWidth === width && nextHeight === height && canvas.width === Math.round(nextWidth * pixelRatio)) return;

      width = nextWidth;
      height = nextHeight;
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    };

    const draw = (now) => {
      const elapsed = Math.max(1, now - lastFrameAt);
      lastFrameAt = now;

      const target = activeRef.current ? targetLevelRef.current : 0;
      // Ataque rápido para a fala e queda suave para não deixar a leitura
      // tremida. O valor de origem continua sendo somente o RMS nativo.
      const response = target > displayedLevel
        ? 1 - Math.exp(-elapsed / 58)
        : 1 - Math.exp(-elapsed / 220);
      displayedLevel += (target - displayedLevel) * response;

      if (now - lastSampleAt >= 58) {
        samples.shift();
        samples.push(displayedLevel);
        lastSampleAt = now;
      }

      resizeCanvas();
      context.clearRect(0, 0, width, height);

      const barCount = Math.max(12, Math.min(LIVE_WAVE_HISTORY, Math.floor(width / 8)));
      const values = samples.slice(-barCount);
      const gap = Math.max(2, Math.min(4, width / (barCount * 3.4)));
      const barWidth = Math.max(2, (width - gap * (barCount - 1)) / barCount);
      const maxHeight = Math.max(12, height - 4);
      const centerY = height / 2;

      values.forEach((sample, index) => {
        // Curva leve apenas amplia os níveis baixos do microfone; não cria
        // movimento onde não existe sinal.
        const amplitude = Math.pow(Math.max(0, Math.min(1, sample)), 0.62);
        const barHeight = 2 + amplitude * maxHeight;
        const x = index * (barWidth + gap);
        const y = centerY - barHeight / 2;
        const hue = 188 + Math.round(amplitude * 108);
        const lightness = 60 + Math.round(amplitude * 16);

        context.fillStyle = `hsla(${hue}, 91%, ${lightness}%, ${0.34 + amplitude * 0.66})`;
        context.shadowColor = `hsla(${hue}, 94%, 67%, ${amplitude * 0.68})`;
        context.shadowBlur = 2 + amplitude * 8;
        drawRoundedBar(context, x, y, barWidth, barHeight, barWidth / 2);
        context.fill();
      });

      context.shadowBlur = 0;
      animationFrame = requestAnimationFrame(draw);
    };

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(resizeCanvas);
    resizeObserver?.observe(canvas);
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    animationFrame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <span className="athena-home-live-waveform" aria-hidden="true">
      <canvas ref={canvasRef} className="athena-home-live-waveform-canvas" />
    </span>
  );
}

function useCaptureDuration(isCapturing) {
  const startedAtRef = useRef(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isCapturing) {
      startedAtRef.current = 0;
      setElapsed(0);
      return undefined;
    }

    startedAtRef.current = Date.now();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startedAtRef.current) / 1000)));
    tick();
    const interval = window.setInterval(tick, 250);
    return () => window.clearInterval(interval);
  }, [isCapturing]);

  return elapsed;
}

/**
 * Compositor da Home para texto e ditado manual.
 *
 * Ele não controla reconhecimento de voz: apenas representa o estado do
 * motor já existente. Assim, o mesmo botão e o núcleo continuam com uma
 * única ação oficial de iniciar/parar/enviar a captura.
 */
export function HomeVoiceComposer({
  textInput,
  onTextInputChange,
  onSubmit,
  onVoiceToggle,
  voiceState,
  voiceMode,
  voiceLevel,
  sessionRestoring = false,
  keyboardOpen = false,
  suppressDeviceContext = false,
  inputRef,
  pendingDeviceItems = [],
  onUseSharedText,
  onOpenSharedAttachments,
  onDiscardSharedContext,
}) {
  const isManualCapture = voiceMode === 'manual' && voiceState === 'active';
  const isProcessing = voiceState === 'processing';
  const isSpeaking = voiceState === 'speaking';
  const elapsed = useCaptureDuration(isManualCapture);
  const normalizedLevel = normalizeVoiceLevel(voiceLevel);

  const placeholder = sessionRestoring
    ? 'Restaurando conversa...'
    : isProcessing
      ? 'Processando...'
      : isSpeaking
        ? 'Respondendo...'
        : 'Toque no núcleo para gravar ou digite...';

  return (
    <div
      className={`athena-home-composer-shell${keyboardOpen ? ' is-keyboard-open' : ''}`}
      data-testid="athena-home-composer"
    >
      {/* A caixa é oculta enquanto há teclado ou gravação para não disputar
          espaço com os controles críticos. O conteúdo nunca é colocado no
          campo sem um toque explícito em "Usar texto". */}
      {!keyboardOpen && !suppressDeviceContext && !isManualCapture && pendingDeviceItems.length > 0 && (
        <DeviceContextTray
          items={pendingDeviceItems}
          onUseText={onUseSharedText}
          onOpenAttachments={onOpenSharedAttachments}
          onDiscard={onDiscardSharedContext}
        />
      )}
      <form onSubmit={onSubmit} className={`voice-bar athena-home-composer${isManualCapture ? ' is-recording' : ''}`}>
        {isManualCapture ? (
          <div
            className="athena-home-recognition"
            role="status"
            aria-live="polite"
            aria-label={`Gravando há ${formatDuration(elapsed)}. Toque novamente para enviar.`}
            data-testid="manual-voice-recognition-bar"
          >
            <span className="athena-home-recognition-time" data-testid="manual-voice-duration">
              {formatDuration(elapsed)}
            </span>
            <LiveAudioWaveform level={normalizedLevel} isActive={isManualCapture} />
          </div>
        ) : (
          <>
            <span className={`athena-home-composer-state state-${voiceState}`} aria-hidden="true" />
            <input
              type="text"
              value={textInput}
              onChange={(event) => onTextInputChange(event.target.value)}
              placeholder={placeholder}
              disabled={sessionRestoring || isProcessing}
              className="athena-home-composer-input"
              ref={inputRef}
              autoComplete="off"
              enterKeyHint="send"
              data-testid="voice-text-input"
            />
          </>
        )}

        <button
          type="button"
          onClick={onVoiceToggle}
          disabled={sessionRestoring || isProcessing}
          aria-label={isManualCapture ? 'Parar e enviar áudio' : 'Ativar microfone'}
          title={isManualCapture ? 'Parar e enviar áudio' : 'Gravar mensagem de voz'}
          className={`athena-home-voice-action${isManualCapture ? ' is-recording' : ''}`}
          data-testid="voice-microphone-button"
        >
          {isManualCapture
            ? <PaperPlaneTilt size={19} weight="fill" aria-hidden="true" />
            : <Microphone size={19} weight="regular" aria-hidden="true" />}
          {isManualCapture && <span className="athena-home-voice-action-label">Enviar</span>}
        </button>

        {!isManualCapture && (
          <span className="athena-home-manual-badge" title="Modo manual ativo">Manual</span>
        )}

        {!isManualCapture && textInput.trim() && (
          <button
            type="submit"
            disabled={sessionRestoring || isProcessing}
            className="athena-home-text-send"
            data-testid="send-button"
          >
            {sessionRestoring ? 'Restaurando...' : 'Enviar'}
          </button>
        )}
      </form>
    </div>
  );
}

export default HomeVoiceComposer;
